// ---------------------------------------------------------------------------
// Rally scoring file format.
//
// The on-disk shape is fixed by the downstream match-scoring consumer, so this
// module writes exactly those keys and nothing else:
//
//   {
//     "NearPlayer": "Carton",
//     "FarPlayer": "Matushkina",
//     "InitialServer": "Matushkina",
//     "AdScoring": true,
//     "Rallies": [
//       { "RallyId": 1, "StartTime": 0.0, "EndTime": 19.1182862,
//         "PointScore": "40-30", "GameScore": "4-2", "SetScore": "1-0",
//         "Server": { "Name": "Matushkina" },
//         "Winner": { "Name": "Carton" } }
//     ]
//   }
//
// In memory a rally records its winner as the side that won ('near' | 'far' |
// null) rather than a name, so editing a player's name retroactively corrects
// every rally. Names are resolved only here, at the file boundary.
//
// The three score strings and Server are derived from the winner sequence by
// rallyScoring.js rather than stored, so they are recomputed on every save.
// Hand-editing them in a file has no effect: they are overwritten next save.
// InitialServer and AdScoring are the two inputs that derivation cannot infer,
// which is why they are written at the top level and restored on load.
// ---------------------------------------------------------------------------

import { computeScoreboard } from './rallyScoring';

export const RALLY_SIDES = ['near', 'far'];

/**
 * Serialize rally state into the NearPlayer/FarPlayer/Rallies file shape.
 * Rallies are ordered by StartTime and renumbered 1..N, so the RallyIds in the
 * file are always contiguous no matter what was deleted or inserted in the UI.
 * A rally whose winner is still unset is written with a null Winner, and every
 * score field from that point on is null because it cannot be derived.
 *
 * @param {object} matchRules - { initialServer: 'near'|'far'|null, adScoring: boolean }
 */
export function serializeRallies(nearPlayer, farPlayer, rallies, matchRules = {}) {
  const { initialServer = null, adScoring = false } = matchRules;

  const nameFor = (side) =>
    side === 'near' ? nearPlayer : side === 'far' ? farPlayer : null;
  const playerRef = (side) => {
    const name = nameFor(side);
    return name === null ? null : { Name: name };
  };

  const ordered = [...rallies].sort((a, b) => a.startTime - b.startTime);
  const scoreboard = computeScoreboard(ordered, initialServer, adScoring);

  return {
    NearPlayer: nearPlayer,
    FarPlayer: farPlayer,
    InitialServer: nameFor(initialServer),
    AdScoring: adScoring,
    Rallies: ordered.map((rally, index) => {
      const score = scoreboard[index];
      return {
        RallyId: index + 1,
        StartTime: rally.startTime,
        EndTime: rally.endTime,
        PointScore: score.pointScore,
        GameScore: score.gameScore,
        SetScore: score.setScore,
        Server: playerRef(score.server),
        Winner: playerRef(rally.winner),
      };
    }),
  };
}

/**
 * Parse a rally file back into in-memory state.
 * The winner is matched back to a side by name. An unrecognised or missing
 * name becomes null so the labeller is prompted to re-pick it rather than
 * silently inheriting the wrong player.
 * Throws if the file is not a rally file.
 */
export function deserializeRallies(data) {
  if (!data || typeof data !== 'object' || !Array.isArray(data.Rallies)) {
    throw new Error('Not a rally file: missing Rallies array');
  }

  const nearPlayer = typeof data.NearPlayer === 'string' ? data.NearPlayer : '';
  const farPlayer = typeof data.FarPlayer === 'string' ? data.FarPlayer : '';

  const sideFor = (name) => {
    if (typeof name !== 'string') return null;
    if (nearPlayer && name === nearPlayer) return 'near';
    if (farPlayer && name === farPlayer) return 'far';
    return null;
  };

  const rallies = data.Rallies.map((rally, index) => ({
    id: `rally-${index}-${rally.RallyId ?? index + 1}`,
    startTime: Number(rally.StartTime) || 0,
    endTime: Number(rally.EndTime) || 0,
    winner: sideFor(rally.Winner?.Name),
  })).sort((a, b) => a.startTime - b.startTime);

  // Files written before InitialServer existed can still be resumed: whoever
  // served the first rally opened the match.
  const firstRally = [...data.Rallies].sort(
    (a, b) => (Number(a.StartTime) || 0) - (Number(b.StartTime) || 0)
  )[0];
  const initialServer =
    sideFor(data.InitialServer) ?? sideFor(firstRally?.Server?.Name);

  return {
    nearPlayer,
    farPlayer,
    rallies,
    initialServer,
    adScoring: typeof data.AdScoring === 'boolean' ? data.AdScoring : false,
  };
}

/** Rally numbers (1-based, in file order) that still have no winner. */
export function incompleteRallyNumbers(rallies) {
  return [...rallies]
    .sort((a, b) => a.startTime - b.startTime)
    .reduce((acc, rally, index) => {
      if (rally.winner === null || rally.winner === undefined) acc.push(index + 1);
      return acc;
    }, []);
}
